import React, { useState, memo } from 'react';
import { Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

function MarkdownView({ children, isStreaming = false }) {
  const [copiedStates, setCopiedStates] = useState({});

  const handleCopy = (text, blockIndex) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedStates((prev) => ({ ...prev, [blockIndex]: true }));
      setTimeout(() => {
        setCopiedStates((prev) => ({ ...prev, [blockIndex]: false }));
      }, 2000);
    });
  };

  return (
    <div className={`markdown-content w-full overflow-x-hidden ${isStreaming ? 'streaming-text' : ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          p: ({ node, ...props }) => <p className="mb-4 leading-7 break-words text-neutral-800 dark:text-neutral-300" {...props} />,
          h1: ({ node, children, ...props }) => (
            <h1 className="text-3xl font-bold mt-6 mb-4 pb-2 border-b border-neutral-200 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100" {...props}>
              {children && children.length > 0 ? children : <span className="sr-only">Section heading</span>}
            </h1>
          ),
          h2: ({ node, children, ...props }) => (
            <h2 className="text-2xl font-semibold mt-5 mb-3 pb-2 border-b border-neutral-200 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100" {...props}>
              {children && children.length > 0 ? children : <span className="sr-only">Section heading</span>}
            </h2>
          ),
          h3: ({ node, children, ...props }) => (
            <h3 className="text-xl font-semibold mt-4 mb-2 text-neutral-900 dark:text-neutral-100" {...props}>
              {children && children.length > 0 ? children : <span className="sr-only">Section heading</span>}
            </h3>
          ),
          ul: ({ node, ...props }) => <ul className="my-4 ml-6 list-disc space-y-2 text-neutral-800 dark:text-neutral-300" {...props} />,
          ol: ({ node, ...props }) => <ol className="my-4 ml-6 list-decimal space-y-2 text-neutral-800 dark:text-neutral-300" {...props} />,
          li: ({ node, ...props }) => <li className="pl-2 text-neutral-800 dark:text-neutral-300" {...props} />,
          a: ({ node, ...props }) => (
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 underline hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              {...props}>
              {props.children && props.children.length > 0 ? props.children : <span className="sr-only">Link</span>}
            </a>
          ),
         

          table: ({ node, ...props }) => (
            <div className="my-6 w-full overflow-x-auto border border-neutral-200 dark:border-neutral-700 rounded-lg">
              <table className="w-full border-collapse" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-neutral-100 dark:bg-neutral-900" {...props} />,
          tbody: ({ node, ...props }) => <tbody {...props} />,
          tr: ({ node, ...props }) => <tr className="border-b border-neutral-200 dark:border-neutral-700 last:border-b-0" {...props} />,
          th: ({ node, ...props }) => <th className="px-4 py-3 text-left font-semibold text-neutral-900 dark:text-neutral-100 border-b-2 border-neutral-300 dark:border-neutral-600" {...props} />,
          td: ({ node, ...props }) => <td className="px-4 py-3 text-left text-neutral-800 dark:text-neutral-300" {...props} />,
            code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');

            if (!inline && match) {
              const codeContent = String(children).replace(/\n$/, '');
              const blockIndex = props['data-sourcepos'] || '0';
              const isCopied = copiedStates[blockIndex];

              return (
                <div className="relative my-4 rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-700 w-full">
                  <div className="flex items-center justify-between bg-neutral-50 dark:bg-neutral-900 px-4 py-2 border-b border-neutral-200 dark:border-neutral-700">
                    <span className="text-sm text-neutral-700 dark:text-neutral-300 font-medium">{match[1]}</span>
                    <button
                      onClick={() => handleCopy(codeContent, blockIndex)}
                      className="bg-white dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 p-1 rounded text-xs text-neutral-700 dark:text-neutral-300 flex items-center gap-1.5 transition-colors duration-200">
                      {isCopied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                      {isCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>

                  <div className="overflow-x-auto w-full">
                    <SyntaxHighlighter
                      style={vscDarkPlus}
                      language={match[1]}
                      PreTag="div"
                      customStyle={{
                        margin: 0,
                        padding: '1rem',
                        fontSize: '0.875rem',
                      }}
                     >
                      {codeContent}
                    </SyntaxHighlighter>
                  </div>
                </div>
              );
            }
            // important to return code and not Pre
            return (
              <code
                className="bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 font-mono text-sm font-medium px-1.5 py-1 rounded break-words"
                {...props}>
                {children}
              </code>
            );
          },
        }}>
        {children}
      </ReactMarkdown>
    </div>
  );
}

export default memo(MarkdownView);
